import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DateSelect from '../components/ui/DateSelect';
import SearchableSelect from '../components/ui/SearchableSelect';
import Icons from '../components/Icons';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import StaffService from '../services/StaffService';
import BulkActionBar from '../components/inventory/BulkActionBar';
import IncidentsKpis from '../components/incidents/IncidentsKpis';
import IncidentsFilterBar from '../components/incidents/IncidentsFilterBar';
import IncidentsTable from '../components/incidents/IncidentsTable';
import IncidentDetailDrawer from '../components/incidents/IncidentDetailDrawer';
import {
  buildIncidentCounts,
  filterIncidents,
  sortIncidents
} from '../components/incidents/incidentUtils';

const buildCsv = (headers, rows) => {
  return [headers, ...rows]
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
        .join(',')
    )
    .join('\n');
};

const downloadCsv = (filename, headers, rows) => {
  const csv = buildCsv(headers, rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const safeList = (service) => {
  if (service && typeof service.list === 'function') return service.list();
  return [];
};

export default function IncidentsPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { incidents, students, incidentController } = useStudentStore();
  const staffService = useMemo(() => new StaffService(), []);
  const staffList = useMemo(() => safeList(staffService), [staffService]);

  const staffOptions = useMemo(
    () =>
      staffList.map((staff) => ({
        value: staff.name,
        label: staff.name,
        meta: staff.role || staff.title || ''
      })),
    [staffList]
  );

  const [filters, setFilters] = useState({
    search: '',
    types: [],
    severities: [],
    statuses: [],
    assignment: 'all',
    followUp: 'any'
  });
  const [activeKpi, setActiveKpi] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeIncidentId, setActiveIncidentId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [assignTargets, setAssignTargets] = useState(null);
  const [assignStaff, setAssignStaff] = useState('');
  const [followUpTargets, setFollowUpTargets] = useState(null);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpPrompt, setFollowUpPrompt] = useState('');

  const studentLookup = useMemo(() => {
    return students.reduce((acc, student) => {
      acc[student.id] = student.name;
      return acc;
    }, {});
  }, [students]);

  const incidentTypes = useMemo(() => {
    return [...new Set(incidents.map((incident) => incident.type))].sort();
  }, [incidents]);

  const severityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'monitoring', label: 'Monitoring' },
    { value: 'closed', label: 'Closed' }
  ];

  const filtered = useMemo(
    () => filterIncidents({ incidents, filters, participantLookup: studentLookup }),
    [incidents, filters, studentLookup]
  );

  const sorted = useMemo(() => sortIncidents(filtered), [filtered]);

  const counts = useMemo(() => buildIncidentCounts(incidents), [incidents]);

  const activeIncident = useMemo(
    () => incidents.find((incident) => incident.id === activeIncidentId) || null,
    [incidents, activeIncidentId]
  );

  const selectedIncidents = useMemo(
    () => incidents.filter((incident) => selectedIds.has(incident.id)),
    [incidents, selectedIds]
  );

  const activeFilters = useMemo(() => {
    const list = [];
    if (filters.search) list.push({ id: 'search', label: `Search: ${filters.search}`, type: 'search' });
    filters.types.forEach((type) =>
      list.push({ id: `type-${type}`, label: `Type: ${type}`, type: 'type', value: type })
    );
    filters.severities.forEach((severity) =>
      list.push({ id: `severity-${severity}`, label: `Severity: ${severity}`, type: 'severity', value: severity })
    );
    filters.statuses.forEach((status) =>
      list.push({ id: `status-${status}`, label: `Status: ${status}`, type: 'status', value: status })
    );
    if (filters.assignment !== 'all') {
      list.push({
        id: 'assignment',
        label: `Assignment: ${filters.assignment === 'assigned' ? 'Assigned' : 'Unassigned'}`,
        type: 'assignment'
      });
    }
    if (filters.followUp !== 'any') {
      list.push({
        id: 'followup',
        label: `Follow-up: ${
          filters.followUp === 'none'
            ? 'None'
            : filters.followUp === 'dueSoon'
            ? 'Due soon'
            : filters.followUp === 'required'
            ? 'Required'
            : 'Overdue'
        }`,
        type: 'followUp'
      });
    }
    return list;
  }, [filters]);

  const updateFilters = (updater) => {
    setActiveKpi(null);
    setFilters((prev) => (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }));
  };

  const clearFilters = () => {
    setActiveKpi(null);
    setFilters({
      search: '',
      types: [],
      severities: [],
      statuses: [],
      assignment: 'all',
      followUp: 'any'
    });
  };

  const handleRemoveFilter = (filter) => {
    if (filter.type === 'search') updateFilters({ search: '' });
    if (filter.type === 'type') updateFilters((prev) => ({ ...prev, types: prev.types.filter((t) => t !== filter.value) }));
    if (filter.type === 'severity') updateFilters((prev) => ({ ...prev, severities: prev.severities.filter((s) => s !== filter.value) }));
    if (filter.type === 'status') updateFilters((prev) => ({ ...prev, statuses: prev.statuses.filter((s) => s !== filter.value) }));
    if (filter.type === 'assignment') updateFilters({ assignment: 'all' });
    if (filter.type === 'followUp') updateFilters({ followUp: 'any' });
  };

  const applyKpiFilter = (kpiId) => {
    setActiveKpi((prev) => {
      const next = prev === kpiId ? null : kpiId;
      setFilters((prevFilters) => {
        const base = { ...prevFilters, statuses: [], severities: [], followUp: 'any' };
        if (!next || next === 'total') return base;
        if (next === 'open') return { ...base, statuses: ['open'] };
        if (next === 'critical') return { ...base, severities: ['critical'] };
        if (next === 'followUps') return { ...base, followUp: 'required' };
        if (next === 'overdue') return { ...base, followUp: 'overdue' };
        return base;
      });
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = (isAllSelected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllSelected) {
        sorted.forEach((incident) => next.delete(incident.id));
      } else {
        sorted.forEach((incident) => next.add(incident.id));
      }
      return next;
    });
  };

  const handleRowClick = (incident) => {
    setActiveIncidentId(incident.id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setActiveIncidentId(null);
    setDrawerOpen(false);
  };

  const openAssignModal = (targets) => {
    setAssignStaff('');
    setAssignTargets(targets);
  };

  const openFollowUpModal = (targets) => {
    setFollowUpDate('');
    setFollowUpPrompt('');
    setFollowUpTargets(targets);
  };

  const handleAssign = () => {
    if (!assignTargets || !assignStaff) return;
    assignTargets.forEach((incident) => {
      incidentController.updateIncident(incident.id, { assignedKeyWorker: assignStaff }, user);
    });
    setAssignTargets(null);
  };

  const handleFollowUp = () => {
    if (!followUpTargets || !followUpDate) return;
    followUpTargets.forEach((incident) => {
      incidentController.updateIncident(
        incident.id,
        {
          followUpRequired: true,
          followUpDate,
          followUpPrompt: followUpPrompt || incident.followUpPrompt
        },
        user
      );
    });
    setFollowUpTargets(null);
  };

  const handleCloseIncident = (incident) => {
    incidentController.closeIncident(incident.id, user);
  };

  const handleUpdateNotes = (incident, notes) => {
    if (!notes || notes === incident.description) return;
    incidentController.updateIncident(incident.id, { description: notes }, user);
  };

  const exportIncidents = (items, filenameSuffix = 'incidents') => {
    const headers = ['ID', 'Type', 'Date', 'Severity', 'Status', 'Assigned', 'Follow-up Date', 'Students'];
    const rows = items.map((incident) => {
      const students = (incident.participants || [])
        .filter((p) => p.type === 'student')
        .map((p) => p.name || studentLookup[p.id] || p.id)
        .join(', ');
      return [
        incident.id,
        incident.type,
        incident.date,
        incident.severity,
        incident.status,
        incident.assignedKeyWorker || 'Unassigned',
        incident.followUpDate || '',
        students
      ];
    });
    downloadCsv(`${filenameSuffix}-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  const bulkEligibility = useMemo(() => {
    const hasSelection = selectedIncidents.length > 0;
    const allOpen = hasSelection && selectedIncidents.every((incident) => incident.status !== 'closed');
    return {
      hasSelection,
      allOpen
    };
  }, [selectedIncidents]);

  const bulkActions = [
    {
      id: 'assign',
      label: 'Assign staff',
      onClick: () => openAssignModal(selectedIncidents),
      disabled: !bulkEligibility.hasSelection,
      disabledReason: bulkEligibility.hasSelection ? '' : 'Select incidents to assign.'
    },
    {
      id: 'followup',
      label: 'Set follow-up',
      onClick: () => openFollowUpModal(selectedIncidents),
      disabled: !bulkEligibility.hasSelection,
      disabledReason: bulkEligibility.hasSelection ? '' : 'Select incidents to set follow-up.'
    },
    {
      id: 'close',
      label: 'Close incidents',
      variant: 'primary',
      onClick: () => selectedIncidents.forEach((incident) => incidentController.closeIncident(incident.id, user)),
      disabled: !bulkEligibility.allOpen,
      disabledReason: bulkEligibility.allOpen ? '' : 'Only open incidents can be closed.'
    },
    {
      id: 'export',
      label: 'Export selected',
      onClick: () => exportIncidents(selectedIncidents, 'incidents-selected'),
      disabled: !bulkEligibility.hasSelection,
      disabledReason: bulkEligibility.hasSelection ? '' : 'Select incidents to export.'
    }
  ];

  return (
    <div className="space-y-4">
      <div className="sp-inventory-header">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Incident Workspace</h2>
          <p className="sp-muted">
            {counts.total} incidents · {counts.open} open · {counts.overdueFollowUps} overdue follow-ups
          </p>
        </div>
        <div className="sp-actions">
          <Button size="sm" variant="secondary" onClick={() => exportIncidents(sorted)}>
            Export CSV
          </Button>
          <Button size="sm" onClick={() => navigate('/incidents/new')} type="button">
            <Icons.Plus /> Log Incident
          </Button>
        </div>
      </div>

      <IncidentsKpis counts={counts} activeKpi={activeKpi} onSelect={applyKpiFilter} />

      <IncidentsFilterBar
        search={filters.search}
        onSearchChange={(value) => updateFilters({ search: value })}
        filters={filters}
        typeOptions={incidentTypes}
        severityOptions={severityOptions}
        statusOptions={statusOptions}
        resultsCount={sorted.length}
        activeFilters={activeFilters}
        onToggleType={(type) =>
          updateFilters((prev) => ({
            ...prev,
            types: prev.types.includes(type) ? prev.types.filter((entry) => entry !== type) : [...prev.types, type]
          }))
        }
        onToggleSeverity={(severity) =>
          updateFilters((prev) => ({
            ...prev,
            severities: prev.severities.includes(severity)
              ? prev.severities.filter((entry) => entry !== severity)
              : [...prev.severities, severity]
          }))
        }
        onToggleStatus={(status) =>
          updateFilters((prev) => ({
            ...prev,
            statuses: prev.statuses.includes(status)
              ? prev.statuses.filter((entry) => entry !== status)
              : [...prev.statuses, status]
          }))
        }
        onAssignmentChange={(value) => updateFilters({ assignment: value })}
        onFollowUpChange={(value) => updateFilters({ followUp: value })}
        onClearFilters={clearFilters}
        onRemoveFilter={handleRemoveFilter}
      />

      <BulkActionBar selectedCount={selectedIncidents.length} actions={bulkActions} />

      <div className="sp-inventory-layout">
        <div className="space-y-3">
          {incidents.length === 0 ? (
            <div className="sp-panel sp-panel--muted p-6">
              <p className="sp-value" style={{ fontWeight: 600 }}>No incidents logged</p>
              <p className="text-sm sp-muted mt-2">Log an incident to start tracking follow-ups.</p>
              <Button size="sm" className="mt-4" onClick={() => navigate('/incidents/new')} type="button">
                <Icons.Plus /> Log Incident
              </Button>
            </div>
          ) : (
            <IncidentsTable
              incidents={sorted}
              participantLookup={studentLookup}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              onRowClick={handleRowClick}
              onViewIncident={(incident) => navigate(`/incidents/${incident.id}`)}
              onEditIncident={handleRowClick}
              onAssignIncident={(incident) => openAssignModal([incident])}
              onCloseIncident={handleCloseIncident}
            />
          )}
        </div>

        <div className="sp-inventory-panel">
          <IncidentDetailDrawer
            incident={activeIncident}
            participantLookup={studentLookup}
            isOpen={drawerOpen}
            onClose={closeDrawer}
            onAssign={(incident) => openAssignModal([incident])}
            onSetFollowUp={(incident) => openFollowUpModal([incident])}
            onCloseIncident={handleCloseIncident}
            onUpdateNotes={handleUpdateNotes}
            onViewHistory={(incident) => navigate(`/incidents/${incident.id}`)}
          />
        </div>
      </div>

      {drawerOpen && <div className="sp-drawer-overlay" onClick={closeDrawer} aria-hidden="true" />}

      <Modal isOpen={Boolean(assignTargets)} onClose={() => setAssignTargets(null)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Assign staff</h3>
              <p className="text-sm sp-muted">Assign a staff member to selected incidents.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setAssignTargets(null)}>Close</Button>
          </div>
          <div>
            <label className="sp-label">Assigned staff</label>
            <SearchableSelect
              value={assignStaff}
              options={staffOptions}
              placeholder="Search staff"
              emptyLabel="No staff found."
              ariaLabel="Search staff"
              onChange={(value) => setAssignStaff(value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAssignTargets(null)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!assignStaff}>Assign</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={Boolean(followUpTargets)} onClose={() => setFollowUpTargets(null)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Set follow-up</h3>
              <p className="text-sm sp-muted">Schedule follow-up date for selected incidents.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setFollowUpTargets(null)}>Close</Button>
          </div>
          <div>
            <label className="sp-label">Follow-up date</label>
            <DateSelect className="w-full mt-2" value={followUpDate} onChange={(value) => setFollowUpDate(value)} />
          </div>
          <div>
            <label className="sp-label">Follow-up notes</label>
            <textarea
              className="sp-textarea w-full"
              rows={3}
              value={followUpPrompt}
              onChange={(event) => setFollowUpPrompt(event.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setFollowUpTargets(null)}>Cancel</Button>
            <Button onClick={handleFollowUp} disabled={!followUpDate}>Set follow-up</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
